/* ExchangeReadinessCard.jsx — calm operational readiness layer.
 *
 * Pure presentation — receives the precomputed readiness object
 * from `computeExchangeReadiness()`. Renders the score, state pill,
 * completed checklist, and outstanding-with-blocker list in the
 * standard ChainFlow card grammar (rounded-2xl · border-slate-100 ·
 * shadow-[rgba(15,23,42,0.02)] · cf-card-hover).
 *
 * Doctrine:
 *   - No gauge, no animated ring — calm linear progress only.
 *   - No alarm colours by default. Only Requires Attention uses red.
 *   - Operational vocabulary only (Exchange Nearing · Progressing
 *     Well · Awaiting Enquiries · Awaiting Legal Response).
 *   - All states explainable from completed/outstanding lists.
 */
import { Check, Clock } from "lucide-react";
import { READINESS_STATE_TONE } from "../../lib/exchangeReadiness";

export function ExchangeReadinessCard({ readiness, testid = "exchange-readiness" }) {
  if (!readiness) return null;
  const { score, state, completed, outstanding, topBlocker } = readiness;
  const tone = READINESS_STATE_TONE[state] || READINESS_STATE_TONE["Progressing Well"];

  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 p-5 cf-card-hover"
      data-testid={testid}
      aria-label="Exchange readiness"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3.5">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500">
            Exchange Readiness
          </p>
          <div className="flex items-baseline gap-2 mt-2">
            <span
              className="text-[28px] font-bold tabular-nums leading-none text-slate-900"
              data-testid={`${testid}-score`}
            >
              {score}
            </span>
            <span className="text-[13px] font-semibold text-slate-500">%</span>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white shrink-0"
          style={{ border: `1px solid ${tone.ring}` }}
          data-testid={`${testid}-state`}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} aria-hidden />
          <span className="text-[11px] font-semibold whitespace-nowrap" style={{ color: tone.fg }}>
            {state}
          </span>
        </span>
      </div>

      {/* Progress rail — calm, single thin line, never a gauge */}
      <div
        className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-4"
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Exchange readiness ${score} percent`}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${score}%`,
            background: tone.dot,
            transitionDuration: "420ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </div>

      {/* Top blocker — single calm sentence */}
      {outstanding.length > 0 && (
        <p
          className="text-[12px] text-slate-500 mb-4"
          data-testid={`${testid}-top-blocker`}
        >
          <span className="text-slate-400">Next · </span>
          <span className="text-slate-700 font-medium">{topBlocker}</span>
        </p>
      )}

      {/* Completed list */}
      <ul className="space-y-1.5 mb-3" data-testid={`${testid}-completed`}>
        {completed.map((m) => (
          <li
            key={m.id}
            className="flex items-center gap-2.5 text-[12.5px]"
            data-testid={`${testid}-complete-${m.id}`}
          >
            <span
              className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0"
              style={{ background: "rgba(16,185,129,0.10)", color: "#059669" }}
              aria-hidden
            >
              <Check size={9} strokeWidth={3} />
            </span>
            <span className="text-slate-700">{m.label}</span>
          </li>
        ))}
      </ul>

      {/* Outstanding list — calm bullets, calm text */}
      {outstanding.length > 0 && (
        <ul className="space-y-1.5" data-testid={`${testid}-outstanding`}>
          {outstanding.map((m) => (
            <li
              key={m.id}
              className="flex items-center gap-2.5 text-[12.5px]"
              data-testid={`${testid}-outstanding-${m.id}`}
            >
              <span
                className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0"
                style={{ background: "#F1F5F9", color: "#94A3B8" }}
                aria-hidden
              >
                <Clock size={9} strokeWidth={2.5} />
              </span>
              <span className="text-slate-500">{m.label}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ExchangeReadinessInline — compact pill version for table rows /
 * exchange cards. Shows score + state in a single line. */
export function ExchangeReadinessInline({ readiness, testid = "exchange-readiness-inline" }) {
  if (!readiness) return null;
  const { score, state } = readiness;
  const tone = READINESS_STATE_TONE[state] || READINESS_STATE_TONE["Progressing Well"];

  return (
    <span
      className="inline-flex items-center gap-2"
      data-testid={testid}
    >
      <span
        className="inline-flex items-center justify-center min-w-[34px] h-5 px-1.5 rounded-md text-[11px] font-bold tabular-nums"
        style={{ background: tone.ring, color: tone.fg }}
      >
        {score}%
      </span>
      <span className="text-[11.5px] text-slate-600 truncate">{state}</span>
    </span>
  );
}
